import React from 'react';
import { EdgeLabelRenderer, getBezierPath } from 'reactflow';
import { Plus, X } from 'lucide-react';
import { useCanvasActions } from 'features/agents/canvas/CanvasActionsContext';

export const EDGE_CURVATURE = 0.45;

/**
 * A step-to-step connection: a smooth bezier with an arrowhead, the branch
 * name (true / false) near its start, and a "+" at its midpoint that inserts
 * a step between the two nodes. The "+" is always there, quiet until hovered.
 *
 * data: { port, pill, from, to, canEdit, runMode, status }
 *   pill: the branch name to print (a route's name); absent means the port
 *   itself, null means none (the node already prints it).
 *   runMode: draw it as part of a run. `status` is the target step's status:
 *   a step that ran took the edge (success colour), none means it was skipped,
 *   running animates it.
 */
export default function FlowEdge({
  id, source, target, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, selected, data,
}) {
  const actions = useCanvasActions();
  const [path, labelX, labelY] = getBezierPath({
    sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, curvature: EDGE_CURVATURE,
  });
  const port = data?.port || null;
  const pill = data && 'pill' in data ? data.pill : port;
  const canEdit = !!data?.canEdit;
  const runMode = !!data?.runMode;
  const status = data?.status || null;

  let state = 'idle';
  if (selected && canEdit) state = 'selected';
  else if (runMode) {
    if (status === 'running' || status === 'queued') state = 'running';
    else if (status) state = 'taken';
    else state = 'skipped';
  }

  const markerId = `flow-arrow-${id}`;
  const ref = { from: data?.from ?? source, to: data?.to ?? target, port };

  return (
    <>
      <defs>
        {/* The marker's colour comes from its own `color`, which a CSS
            variable can drive; a marker `color` prop on the edge cannot. */}
        <marker
          id={markerId}
          className={`flow-edge-arrow is-${state}`}
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="10"
          markerHeight="10"
          markerUnits="userSpaceOnUse"
          orient="auto"
        >
          <path d="M1 1.5 L9 5 L1 8.5 z" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" />
        </marker>
      </defs>
      {/* BaseEdge ignores className in v11, and the state class is what the CSS
          colours by: draw the path and its wider hit area directly. */}
      <path
        id={id}
        d={path}
        fill="none"
        className={`react-flow__edge-path flow-edge-path is-${state}`}
        markerEnd={`url(#${markerId})`}
      />
      <path d={path} fill="none" strokeOpacity={0} strokeWidth={18} className="react-flow__edge-interaction" />
      <EdgeLabelRenderer>
        {pill && (
          <span
            className="flow-edge-pill nodrag nopan"
            style={{ transform: `translate(-50%, -50%) translate(${sourceX}px, ${sourceY + 14}px)` }}
          >
            {pill}
          </span>
        )}
        {canEdit && (
          <span
            className="flow-edge-tools nodrag nopan"
            style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }}
          >
            <button
              type="button"
              className="flow-edge-btn"
              title="Insert a step"
              aria-label="Insert a step here"
              onClick={(e) => { e.stopPropagation(); actions.onInsertOnEdge(ref); }}
            >
              <Plus size={11} strokeWidth={2} />
            </button>
            {selected && (
              <button
                type="button"
                className="flow-edge-btn"
                title="Remove connection"
                aria-label="Remove this connection"
                onClick={(e) => { e.stopPropagation(); actions.onRemoveEdge(ref); }}
              >
                <X size={11} strokeWidth={2} />
              </button>
            )}
          </span>
        )}
      </EdgeLabelRenderer>
    </>
  );
}
